/* eslint-disable jsx-a11y/label-has-associated-control */
import { FC } from 'react';
import { createAdminPageStyles, MUTED } from '../../style';
import { ConsultantSummary, ServiceOption } from '../types';
import { hasMultipleStaffRoleGroups, staffRoleGroupBy } from '../utils';
import SearchAutocomplete from './SearchAutocomplete';

interface PrestationFieldsProps {
  consultants: ConsultantSummary[];
  consultantId: number | '';
  lockConsultant?: boolean;
  disableConsultantWhenEmpty?: boolean;
  onConsultantChange: (id: number | '') => void;
  consultantWarning?: string;
  services: ServiceOption[];
  serviceId: number | '';
  onServiceChange: (id: number | '') => void;
  attachedServiceIds?: number[];
  disableServiceWhenNoneSelectable?: boolean;
  serviceWarning?: string;
  infoBox?: string;
}

const useStyles = createAdminPageStyles({
  warningBox: {
    background: '#FFF4E5',
    color: '#8A5A00',
    borderRadius: 10,
    padding: '14px 18px',
    fontFamily: 'Poppins',
    fontSize: 14,
    lineHeight: 1.55,
    marginTop: 12,
  },
  disabledOption: {
    color: MUTED,
    fontStyle: 'italic',
  },
});

const consultantLabel = (consultant: ConsultantSummary) =>
  `${consultant.firstname || ''} ${consultant.lastname || ''}`.trim();

const PrestationFields: FC<PrestationFieldsProps> = ({
  consultants,
  consultantId,
  lockConsultant = false,
  disableConsultantWhenEmpty = false,
  onConsultantChange,
  consultantWarning,
  services,
  serviceId,
  onServiceChange,
  attachedServiceIds = [],
  disableServiceWhenNoneSelectable = false,
  serviceWarning,
  infoBox,
}) => {
  const classes = useStyles();
  const selectedService = services.find(item => Number(item.id) === Number(serviceId)) || null;
  const selectedConsultant =
    consultants.find(item => Number(item.id) === Number(consultantId)) || null;
  const attachedSet = new Set(attachedServiceIds.map(Number));
  const selectableServices = services.filter(service => !attachedSet.has(Number(service.id)));
  const consultantDisabled =
    lockConsultant || (disableConsultantWhenEmpty && consultants.length === 0);
  const serviceDisabled =
    !consultantId || (disableServiceWhenNoneSelectable && selectableServices.length === 0);
  const groupConsultants = hasMultipleStaffRoleGroups(consultants);

  return (
    <div>
      {infoBox ? <div className={classes.infoBox}>{infoBox}</div> : null}

      <h2 className={classes.sectionTitle}>Prestation</h2>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="consultant-select">
          Administrateur ou consultant
        </label>
        <SearchAutocomplete
          id="consultant-select"
          options={consultants}
          value={selectedConsultant}
          disabled={consultantDisabled}
          placeholder="Rechercher un nom ou un prénom"
          getOptionLabel={consultantLabel}
          groupBy={groupConsultants ? staffRoleGroupBy : undefined}
          onChange={selected => {
            const consultant = selected as ConsultantSummary | null;
            onConsultantChange(consultant ? Number(consultant.id) : '');
          }}
        />
        {consultantWarning ? <div className={classes.warningBox}>{consultantWarning}</div> : null}
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="service-select">
          Prestation
        </label>
        <SearchAutocomplete
          id="service-select"
          options={services}
          value={selectedService}
          disabled={serviceDisabled}
          placeholder="Rechercher une prestation"
          getOptionLabel={option => option.name}
          getOptionDisabled={option => attachedSet.has(Number(option.id))}
          onChange={selected => {
            const service = selected as ServiceOption | null;
            onServiceChange(service ? Number(service.id) : '');
          }}
          renderOption={option => (
            <span className={attachedSet.has(Number(option.id)) ? classes.disabledOption : undefined}>
              {option.name}
              {attachedSet.has(Number(option.id)) ? ' (déjà rattachée)' : ''}
            </span>
          )}
        />
        {serviceWarning ? <div className={classes.warningBox}>{serviceWarning}</div> : null}
      </div>
    </div>
  );
};

export default PrestationFields;
